import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { injectGlobal } from '@emotion/css';
import { t } from 'i18next';
import { getTourConfigForRoute } from '../config/tourConfigs';

const font = "'Roboto', 'Helvetica', 'Arial', sans-serif";

/**
 * `injectGlobal` läuft einmal beim Modul-Import, ausserhalb von React — diese
 * Regeln sehen das MUI-Theme nie und reagieren nicht auf einen Moduswechsel.
 * Die Farben laufen deshalb über die `--ly-tour-*`-Variablen aus index.css,
 * dasselbe Muster wie beim übrigen globalen UI-Chrome (Ticket 4/8).
 *
 * Die Fläche allein umzustellen hätte nicht gereicht: Pfeile, Footer, Buttons
 * und der Close-Button hängen alle an ihr — ein dunkles Popover mit weissen
 * Pfeilspitzen und schwarzem Titel wäre halb kaputt gewesen.
 */
const styles = {
  '.driver-popover.ly-tour-popover': {
    fontFamily: font,
    backgroundColor: 'var(--ly-tour-bg)',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.18), 0 2px 8px rgba(58,32,205,0.1)',
    padding: 0,
    minWidth: '320px',
    maxWidth: '360px',
    overflow: 'hidden',
    border: 'none',
  },
  '.driver-popover.ly-tour-popover *': {
    fontFamily: font,
  },
  '.driver-popover.ly-tour-popover::before': {
    content: '""',
    display: 'block',
    height: '4px',
    background: 'linear-gradient(90deg, #3a20cd, #9f2ccc)',
    width: '100%',
  },
  '.driver-popover.ly-tour-popover .driver-popover-title': {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--ly-tour-text)',
    padding: '16px 40px 4px 16px',
    lineHeight: 1.4,
  },
  '.driver-popover.ly-tour-popover .driver-popover-description': {
    fontSize: '13.5px',
    color: 'var(--ly-tour-text)',
    padding: '0 16px 4px',
    lineHeight: 1.6,
    margin: 0,
  },
  '.driver-popover.ly-tour-popover .driver-popover-footer': {
    padding: '10px 16px 14px',
    marginTop: 0,
    borderTop: '1px solid var(--ly-tour-border)',
    background: 'var(--ly-tour-footer-bg)',
  },
  '.driver-popover.ly-tour-popover .driver-popover-progress-text': {
    fontSize: '12px',
    color: 'var(--ly-tour-accent-text)',
    fontWeight: 500,
  },
  '.driver-popover.ly-tour-popover .driver-popover-footer button': {
    fontFamily: font,
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.02em',
    padding: '5px 14px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s, box-shadow 0.2s',
    textShadow: 'none',
    lineHeight: 1.5,
  },
  '.driver-popover.ly-tour-popover .driver-popover-next-btn, .driver-popover.ly-tour-popover .driver-popover-done-btn': {
    backgroundColor: 'var(--ly-tour-accent)',
    color: '#fff !important',
    border: '1px solid var(--ly-tour-accent)',
  },
  '.driver-popover.ly-tour-popover .driver-popover-next-btn:hover, .driver-popover.ly-tour-popover .driver-popover-done-btn:hover': {
    backgroundColor: 'var(--ly-tour-accent-hover)',
    borderColor: 'var(--ly-tour-accent-hover)',
    boxShadow: '0 2px 6px rgba(58,32,205,0.35)',
  },
  '.driver-popover.ly-tour-popover .driver-popover-prev-btn': {
    backgroundColor: 'transparent',
    color: 'var(--ly-tour-accent) !important',
    border: '1px solid var(--ly-tour-accent-soft)',
  },
  '.driver-popover.ly-tour-popover .driver-popover-prev-btn:hover': {
    backgroundColor: 'var(--ly-tour-accent-wash)',
    borderColor: 'var(--ly-tour-accent)',
  },
  '.driver-popover.ly-tour-popover .driver-popover-btn-disabled': {
    opacity: '0.4 !important',
    pointerEvents: 'none',
  },
  '.driver-popover.ly-tour-popover .driver-popover-close-btn': {
    color: 'var(--ly-tour-muted)',
    top: '8px',
    right: '8px',
    fontSize: '16px',
    width: '28px',
    height: '28px',
  },
  '.driver-popover.ly-tour-popover .driver-popover-close-btn:hover': {
    color: 'var(--ly-tour-accent)',
  },
  '.driver-popover.ly-tour-popover .driver-popover-arrow-side-left': { borderLeftColor: 'var(--ly-tour-bg)' },
  '.driver-popover.ly-tour-popover .driver-popover-arrow-side-right': { borderRightColor: 'var(--ly-tour-bg)' },
  '.driver-popover.ly-tour-popover .driver-popover-arrow-side-top': { borderTopColor: 'var(--ly-tour-bg)' },
  '.driver-popover.ly-tour-popover .driver-popover-arrow-side-bottom': { borderBottomColor: 'var(--ly-tour-bg)' },
  // the highlight is a cutout in the overlay; a background on the overlay element itself also covered
  // the cutout, so the highlighted element sat under a tint and everything else under two
  '.driver-active-element': {
    outline: '2px solid var(--ly-tour-accent)',
    outlineOffset: '6px',
    borderRadius: '4px',
  },
  '.ly-tour-switcher-btn': {
    fontFamily: font,
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.02em',
    padding: '5px 14px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    backgroundColor: 'transparent',
    color: 'var(--ly-tour-accent2) !important',
    border: '1px solid var(--ly-tour-accent2-soft)',
    marginLeft: '8px',
  },
  '.ly-tour-switcher-btn:hover': {
    backgroundColor: 'var(--ly-tour-accent2-wash)',
    borderColor: 'var(--ly-tour-accent2)',
  },
};

injectGlobal(styles);

const TOUR_STORAGE_KEY = 'ly_tour_completed';

export const hasTourBeenSeen = () => !!window.localStorage.getItem(TOUR_STORAGE_KEY);
const markTourAsSeen = () => window.localStorage.setItem(TOUR_STORAGE_KEY, 'true');

let _currentDriverObj = null;

export const startTour = (config) => {
  const tourConfig = config ?? getTourConfigForRoute(window.location.pathname);
  if (!tourConfig) return;

  const needsDelay = tourConfig.onBeforeStart?.() ?? false;

  const launch = () => {
    const steps = tourConfig.steps({ startTour }).map(step => ({
      ...step,
      popover: {
        ...step.popover,
      },
      onHighlightStarted: step.onHighlightStarted
        ? () => step.onHighlightStarted(_currentDriverObj)
        : undefined,
      onDeselected: step.onDeselected
        ? () => step.onDeselected(_currentDriverObj)
        : undefined,
    }));

    if (steps.length === 0) return;

    const scrollHandler = () => driverObj.refresh();

    const driverObj = driver({
      animate: true,
      overlayColor: 'rgb(10, 5, 40)',
      overlayOpacity: 0.4,
      stagePadding: 8,
      allowClose: true,
      // a click anywhere on the dimmed page moves on, so Next doesn't have to be found each step;
      // it also no longer ends the tour by accident
      // runs the step's own Next handler where there is one (the section tour opens an entry there);
      // driver's built-in 'nextStep' skips it
      overlayClickBehavior: (element, step, options) => {
        if (step.popover?.onNextClick) step.popover.onNextClick(element, step, options);
        else options.driver.moveNext();
      },
      popoverClass: 'ly-tour-popover',
      showProgress: true,
      progressText: '{{current}}/{{total}}',
      nextBtnText: t('onboarding.tour.nextBtn'),
      prevBtnText: t('onboarding.tour.prevBtn'),
      doneBtnText: t('onboarding.tour.doneBtn'),
      steps,
      onDestroyStarted: () => {
        if (window.mainFrameRef) {
          window.mainFrameRef.removeEventListener('scroll', scrollHandler);
        }
        markTourAsSeen();
        driverObj.destroy();
        _currentDriverObj = null;
      },
    });

    if (window.mainFrameRef) {
      window.mainFrameRef.addEventListener('scroll', scrollHandler);
    }

    _currentDriverObj = driverObj;
    driverObj.drive();
  };

  if (needsDelay) {
    setTimeout(launch, 350);
  } else {
    launch();
  }
};
